import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import ChartCard from "./ChartCard";
import {
  formatCount,
  formatRankedValue,
  getChartCardStatus,
} from "./charts.utils";
import { getValueRange } from "./countries.utils";
import CountriesMap from "./CountriesMap";
import ExportCsvButton from "./ExportCsvButton";
import MapTooltipRow from "./MapTooltipRow";
import useWorldMap from "./useWorldMap";

import type { JSX, ReactNode } from "react";

import type { Query } from "@/src/api/BaseAPI";

import type { CsvExport } from "./charts.types";

interface CountriesMapQueryCardProps<TResponse> {
  title: string;
  query: Query<TResponse>;
  /** Must keep a stable identity (useCallback) to preserve the memo. */
  buildValueByCountry: (
    response: TResponse | undefined,
  ) => Record<string, number>;
  valueLabel: string;
  renderExtraTooltipRows?: (countryCode: string) => ReactNode;
  csvExport: CsvExport;
  extraActions?: ReactNode;
}

function CountriesMapQueryCard<TResponse>({
  title,
  query,
  buildValueByCountry,
  valueLabel,
  renderExtraTooltipRows,
  csvExport,
  extraActions,
}: CountriesMapQueryCardProps<TResponse>): JSX.Element {
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;
  useWorldMap();
  const { data, isLoading, isError, isFetching } = useQuery(query);

  const valueByCountry = useMemo(
    () => buildValueByCountry(data),
    [buildValueByCountry, data],
  );
  const grandTotal = useMemo(
    () => Object.values(valueByCountry).reduce((sum, value) => sum + value, 0),
    [valueByCountry],
  );
  const formatValue = useCallback(
    (value: number) => formatCount(value, lang),
    [lang],
  );
  const renderTooltip = (code: string): ReactNode => (
    <React.Fragment>
      <MapTooltipRow
        label={valueLabel}
        value={formatRankedValue(valueByCountry[code] ?? 0, grandTotal, lang)}
      />
      {renderExtraTooltipRows?.(code)}
    </React.Fragment>
  );

  return (
    <ChartCard
      isFetching={isFetching}
      title={title}
      actions={
        <React.Fragment>
          {extraActions}
          <ExportCsvButton
            fetchCsv={csvExport.fetchCsv}
            fileName={csvExport.fileName}
          />
        </React.Fragment>
      }
      status={getChartCardStatus({
        isLoading,
        isError,
        isEmpty: getValueRange(valueByCountry) === null,
      })}
    >
      <CountriesMap
        ariaLabel={t("charts.countriesMap")}
        formatValue={formatValue}
        renderTooltip={renderTooltip}
        valueByCountry={valueByCountry}
      />
    </ChartCard>
  );
}

export default CountriesMapQueryCard;
